import { useState, useEffect, useRef } from "react";
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import {
  MessageSquare,
  Search,
  X,
  Plus,
  ChevronRight,
  ChevronLeft,
  MoreVertical,
  Star,
  Edit,
  Trash,
  Code,
  ChevronDown,
  Filter,
  Sun,
  History,
  Archive,
} from "lucide-react";
import DeleteConfirmDialog from "./common/DeleteConfirmDialog";
import { useThreads, useThreadsLoading, useThreadsFetching, useThreadsError, useThreadActions } from '../store/threadsStore';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { Tooltip } from '@mui/material';

// Custom scrollbar styles
const scrollbarStyles = `
  /* WebKit browsers (Chrome, Safari, Edge) */
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    transition: background-color 0.2s ease;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.25);
  }

  /* Dark mode */
  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.25);
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
  }

  .dark .custom-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  }

  /* Smooth scrolling */
  .custom-scrollbar {
    scroll-behavior: smooth;
  }
  
  /* Hide scrollbar utility */
  .hide-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  
  .hide-scrollbar::-webkit-scrollbar {
    display: none;  /* Chrome, Safari and Opera */
  }
`;

// Inject styles into the document head
const injectScrollbarStyles = () => {
  if (typeof document !== 'undefined') {
    const existingStyle = document.getElementById('custom-scrollbar-styles');
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = 'custom-scrollbar-styles';
      style.textContent = scrollbarStyles;
      document.head.appendChild(style);
    }
  }
};

// Rename Dialog with Apple-like design
const RenameDialog = ({ open, title, initialValue, onConfirm, onClose }) => {
  const [value, setValue] = useState(initialValue);
  
  useEffect(() => {
    if (open) {
      setValue(initialValue);
    }
  }, [open, initialValue]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(value);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop with blur */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-slate-100 dark:bg-neutral-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
          
          <div className="relative p-6">
            <h3 className="text-base font-semibold text-neutral-900 dark:text-white mb-4">{title}</h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-neutral-800 border border-slate-300 dark:border-neutral-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent transition-all text-neutral-900 dark:text-white placeholder-neutral-500"
                autoFocus
              />
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-slate-600 dark:text-neutral-300 bg-slate-300 dark:bg-neutral-700 hover:bg-slate-400 dark:hover:bg-neutral-600 rounded-xl font-medium transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-brand-action hover:bg-brand-action-hover text-brand-action-text rounded-xl font-medium transition-all shadow-sm"
                >
                  Rename
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const ThreadsPanel = ({
  workspaceId,
  sidebarOpen,
  setSidebarOpen,
  navigate,
  searchQuery,
  setSearchQuery,
  getAgentTypeLabel,
  getAgentTypeIcon,
  getAgentTypeColor,
  threadId,
  'data-tour': dataTour,
}) => {
  // Use thread actions directly in component
  const authenticatedApi = useAuthenticatedApi();
  const { createThread, updateThread, deleteThread } = useThreadActions();
  // Get threads data from global store
  const threads = useThreads();
  const loading = useThreadsLoading();        // CRUD operations loading
  const fetchingThreads = useThreadsFetching(); // List fetching loading
  const error = useThreadsError();
  // Simplified color scheme
  const uiColors = {
    bg: 'bg-neutral-100/80 dark:bg-neutral-800/30',
    hover: 'hover:bg-neutral-200 dark:hover:bg-neutral-700/50',
    text: 'text-neutral-700 dark:text-neutral-300',
    icon: 'text-neutral-600 dark:text-neutral-400',
    border: 'border-neutral-300 dark:border-neutral-700/50'
  };

  // Subtle contextual colors for group icons (only when active)
  const groupIconColors = {
    starred: 'text-amber-500 dark:text-amber-400',
    today: 'text-neutral-600 dark:text-neutral-400',
    yesterday: 'text-neutral-600 dark:text-neutral-400',
    older: 'text-stone-500 dark:text-stone-400'
  };

  // Inject scrollbar styles on component mount
  useEffect(() => {
    injectScrollbarStyles();
  }, []);

  // Track if screen is mobile sized
  const [isMobileView, setIsMobileView] = useState(false);

  // Handle mobile view detection and resize
  useEffect(() => {
    const handleResize = () => {
      // Check if mobile view (less than 768px - md breakpoint)
      const mobileViewDetected = window.innerWidth < 768;
      setIsMobileView(mobileViewDetected);
    };

    // Check on initial load
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    starred: true,
    today: true,
    yesterday: true,
    older: false
  });
  
  // State for starred threads
  const [starredThreads, setStarredThreads] = useState([]);
  
  // Load starred threads from localStorage on component mount
  useEffect(() => {
    try {
      const savedStarred = localStorage.getItem('starredThreads');
      if (savedStarred) {
        setStarredThreads(JSON.parse(savedStarred));
      }
    } catch (error) {
      console.error('Error loading starred threads:', error);
    }
  }, []);
  const [showSearch, setShowSearch] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const threadListRef = useRef(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    threadId: null,
    onConfirm: null,
  });
  const [renameDialog, setRenameDialog] = useState({
    open: false,
    title: "Rename Thread",
    threadId: null,
    initialValue: "",
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId) {
        const isClickOnDropdownMenu = event.target.closest('[role="menu"]');
        const isClickOnAnyTriggerButton = event.target.closest('[data-dropdown-trigger]');
        
        if (!isClickOnDropdownMenu && !isClickOnAnyTriggerButton) {
          setOpenDropdownId(null);
        }
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);


  // Group threads by date: today, yesterday, older
  // Function to determine which group contains the active thread
  const getActiveThreadGroup = () => {
    if (!threadId) return null;

    const activeThread = threads.find(thread => thread.id === threadId);
    if (!activeThread) return null;

    if (starredThreads.includes(activeThread.id)) return 'starred';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const threadDate = activeThread.updated_at ? new Date(activeThread.updated_at) : new Date(activeThread.created_at || Date.now());
    threadDate.setHours(0, 0, 0, 0);

    if (threadDate.getTime() === today.getTime()) return 'today';
    if (threadDate.getTime() === yesterday.getTime()) return 'yesterday';
    return 'older';
  };

  const groupThreadsByDate = (threadsToGroup) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const groups = {
      starred: [],
      today: [],
      yesterday: [],
      older: []
    };
    
    // First separate starred and non-starred threads
    const starredThreadIds = new Set(starredThreads);
    const nonStarredThreads = [];
    
    threadsToGroup.forEach(thread => {
      if (starredThreadIds.has(thread.id)) {
        groups.starred.push(thread);
      } else {
        nonStarredThreads.push(thread);
      }
    });
    
    // Then group non-starred threads by date
    nonStarredThreads.forEach(thread => {
      const threadDate = thread.updated_at ? new Date(thread.updated_at) : new Date(thread.created_at || Date.now());
      threadDate.setHours(0, 0, 0, 0);
      
      if (threadDate.getTime() === today.getTime()) {
        groups.today.push(thread);
      } else if (threadDate.getTime() === yesterday.getTime()) {
        groups.yesterday.push(thread);
      } else {
        groups.older.push(thread);
      }
    });
    
    return groups;
  };
  
  const filterThreads = () => {
    if (!searchQuery.trim()) return threads;

    return threads.filter((thread) =>
      thread.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };
  
  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleDropdown = (e, id, index, total) => {
    e.stopPropagation();

    const position = index > total / 2 ? "top" : "bottom";
    setDropdownPosition(position);

    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleMenuAction = (e, action, clickedThreadId) => {
    e.stopPropagation();
    setOpenDropdownId(null);
    
    if (action === 'delete') {
      // Prevent deleting the only thread
      if ((threads?.length || 0) <= 1) {
        console.warn('Cannot delete the only thread');
        return;
      }
      if (typeof deleteThread !== 'function') {
        console.error('deleteThread function is not available');
        return;
      }
      
      const threadToDelete = threads.find(thread => 
        (thread.id) === clickedThreadId
      );
      
      if (threadToDelete) {
        setDeleteConfirmDialog({
          open: true,
          title: "Delete Thread",
          message: `Are you sure you want to delete the thread "${threadToDelete.title}"? This action cannot be undone.`,
          threadId: clickedThreadId,
          onConfirm: async () => {
            try {
              const success = await deleteThread(workspaceId, clickedThreadId, authenticatedApi);
              
              if (success) {
                // If the thread was starred, remove it from starred threads
                if (starredThreads.includes(clickedThreadId)) {
                  const newStarredThreads = starredThreads.filter(id => id !== clickedThreadId);
                  setStarredThreads(newStarredThreads);
                  localStorage.setItem('starredThreads', JSON.stringify(newStarredThreads));
                }
                
                if (clickedThreadId === threadId) {
                  const remaining = (threads || [])
                    .filter(t => (t.id) !== clickedThreadId)
                    .sort((a, b) => {
                      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
                      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
                      return bTime - aTime;
                    });

                  const next = remaining[0];
                  if (next?.id) {
                    navigate(`/project/${workspaceId}/thread/${next.id}`);
                  }
                }
              }
            } catch (error) {
              console.error("Failed to delete thread:", error);
            }
          }
        });
      }
    } else if (action === 'rename') {
      if (typeof updateThread !== 'function') {
        console.error('updateThread function is not available');
        return;
      }
      
      const threadToRename = threads.find(thread => 
        (thread.id) === clickedThreadId
      );
      
      if (threadToRename) {
        setRenameDialog({
          open: true,
          title: "Rename Thread",
          threadId: clickedThreadId,
          initialValue: threadToRename.title,
        });
      }
    } else if (action === 'star') {
      // Toggle star status
      let newStarredThreads;
      
      if (starredThreads.includes(clickedThreadId)) {
        // Unstar the thread
        newStarredThreads = starredThreads.filter(id => id !== clickedThreadId);
      } else {
        // Star the thread
        newStarredThreads = [...starredThreads, clickedThreadId];
      }
      
      // Update state and save to localStorage
      setStarredThreads(newStarredThreads);
      localStorage.setItem('starredThreads', JSON.stringify(newStarredThreads));
    } else {
      console.log(`${action} thread: ${clickedThreadId}`);
    }
  };

  const handleRenameThread = async (newTitle) => {
    if (!renameDialog.threadId || !newTitle.trim()) return;
    
    try {
      const success = await updateThread(workspaceId, renameDialog.threadId, { title: newTitle }, authenticatedApi);
      if (!success) {
        console.error("Failed to rename thread");
      }
    } catch (error) {
      console.error("Error renaming thread:", error);
    } finally {
      setRenameDialog(prev => ({ ...prev, open: false }));
    }
  };

  const { isReadOnly } = useReadOnlyMode();

  const handleNewThreadClick = async () => {
    if (isReadOnly) return;
    try {
      const threadData = {
        title: `New Thread`,
        agent_type: 'coding_agent',
        auto_submit_first_message: false,
        message_history: []
      };
      
      const createdThread = await createThread(workspaceId, threadData, authenticatedApi);
      
      if (createdThread) {
        const threadId = createdThread.id;
        if (threadId) {
          navigate(`/project/${workspaceId}/thread/${threadId}`);
        } else {
          console.error('Created thread has no ID');
        }
      }
    } catch (error) {
      console.error('Error creating thread:', error);
    }
  };

  return (
    <aside
      className={`flex-shrink-0 bg-slate-100 dark:bg-neutral-900 border-r border-slate-300 dark:border-neutral-800 flex flex-col h-full transition-all duration-300 ${
        sidebarOpen ? "w-64" : "w-14"
      } ${isMobileView && !sidebarOpen ? "relative z-30 shadow-md" : ""}`}
    >
      {sidebarOpen ? (
        <>
          {/* Expanded view */}
          <div className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <MessageSquare className="mr-2 text-ui-text-secondary" size={14} />
                <h2 className="text-ui-text-primary text-sm font-semibold dark:text-white" {...(dataTour && { 'data-tour': dataTour })}>
                  Threads
                </h2>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  className="p-1.5 hover:bg-slate-200 dark:hover:bg-neutral-800 rounded-lg transition-colors text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                  onClick={() => setShowSearch(!showSearch)}
                  title="Search threads"
                >
                  <Search size={16} />
                </button>
                <button
                  className="p-1 hover:bg-slate-200 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                  onClick={() => setSidebarOpen(false)}
                  title="Collapse panel"
                >
                  <ChevronLeft
                    size={16}
                    className="text-neutral-500 dark:text-neutral-400"
                  />
                </button>
              </div>
            </div>
            {showSearch && (
              <div className="relative mb-2">
                <div className="relative flex items-center">
                  <Search
                    size={14}
                    className="absolute left-3 text-neutral-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    placeholder="Search threads..."
                    className="w-full bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-white rounded-lg px-3 py-2 pl-9 pr-8 text-sm outline-none border border-neutral-200 dark:border-neutral-700 focus:ring-2 focus:ring-lime-500 focus:border-transparent transition-all placeholder:text-neutral-400"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                  {searchQuery && (
                    <button 
                      className="absolute right-3 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <div className="flex items-center justify-between mt-1.5 px-1">
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                      {filterThreads().length} result{filterThreads().length !== 1 ? 's' : ''}
                    </span>
                    <button 
                      className="flex items-center text-xs text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
                      onClick={() => setSearchQuery('')}
                    >
                      <Filter size={10} className="mr-1" />
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Expanded threads list with custom scrollbar */}
          <div className="flex-1 overflow-y-auto px-3 py-2 custom-scrollbar" ref={threadListRef}>
            {fetchingThreads ? (
              <div className="text-center p-3 text-xs text-neutral-500">
                Loading threads...
              </div>
            ) : error ? (
              <div className="text-center p-3 text-xs text-red-500">
                {error.message}
              </div>
            ) : filterThreads().length === 0 ? (
              <div className="text-center p-3 text-xs text-neutral-500">
                No threads found
              </div>
            ) : (
              Object.entries(groupThreadsByDate(filterThreads())).map(([timeframe, threadsInGroup]) => {
                if (threadsInGroup.length === 0) return null;
                
                return (
                  <div key={timeframe} className={`mb-4 rounded-lg ${uiColors.bg} ${expandedSections[timeframe] ? uiColors.border : ''}`}>
                    <div
                      className={`flex items-center justify-between px-3 py-1.5 cursor-pointer rounded-lg ${uiColors.bg} ${expandedSections[timeframe] ? uiColors.border : ''}`}
                      onClick={() => toggleSection(timeframe)}
                    >
                      <div className="flex items-center">
                        <div className="flex items-center justify-center w-5 h-5 mr-2">
                          {timeframe === 'starred' && <Star size={14} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.starred : uiColors.icon} ${timeframe === 'starred' ? 'fill-current' : ''}`} />}
                          {timeframe === 'today' && <Sun size={14} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.today : uiColors.icon}`} />}
                          {timeframe === 'yesterday' && <History size={14} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.yesterday : uiColors.icon}`} />}
                          {timeframe === 'older' && <Archive size={14} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.older : uiColors.icon}`} />}
                        </div>
                        <h4 className={`text-xs font-medium uppercase tracking-wider ${uiColors.text}`}>
                          {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
                          <span className="ml-1.5 font-normal opacity-80">({threadsInGroup.length})</span>
                        </h4>
                      </div>
                      <button className="p-0.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors">
                        {expandedSections[timeframe] ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </button>
                    </div>
                    
                    {expandedSections[timeframe] && (
                      <div className="mt-2 space-y-2 px-1">
                        {threadsInGroup.map((thread, index) => (
                         <div
                           key={thread.id}
                           className={`px-3 py-2 rounded-lg cursor-pointer mb-2 group relative ${
                             thread.id === threadId
                               ? "bg-slate-200 dark:bg-neutral-800 border-l-2 border-neutral-400 dark:border-neutral-500 shadow-sm pl-2"
                               : "hover:bg-slate-200 dark:hover:bg-neutral-800/50"
                           }`}
                           onClick={() => {
                             navigate(`/project/${workspaceId}/thread/${thread.id}`);
                           }}
                           data-tour="thread-item"
                          >
                            <div className="flex flex-col">
                              <div className="flex items-center justify-between w-full">
                                <div className="flex items-center min-w-0 mr-2">

                                  {/* Recording indicator - always visible, green when active, gray glass when inactive */}
                                  <div className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${
                                    thread.running_jobs && thread.running_jobs.length > 0 
                                      ? 'bg-green-600 animate-pulse shadow-lg shadow-green-500/50' 
                                      : 'bg-gradient-to-br from-gray-400 to-gray-600 shadow-inner border border-gray-300/30 backdrop-blur-sm dark:from-gray-500 dark:to-gray-700 dark:border-gray-600/30'
                                  }`}></div>

                                  {thread.task_id && (
                                    <span className="text-xs flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full px-2 py-0.5 font-medium mr-2">
                                      Task
                                    </span>
                                  )}
                                  <div className="flex items-center min-w-0">
                                  <Tooltip title={`${thread.title} [${thread.created_at ? new Date(thread.created_at).toLocaleString() : 'No date'}]`} enterDelay={1000} placement="right">
                                    <span className="truncate text-sm text-neutral-900 dark:text-white">
                                      {thread.title}
                                    </span>
                                  </Tooltip>
                                  </div>
                                </div>
                                <div className="flex items-center ml-auto">
                                  <div className="relative">
                                    <button
                                      data-dropdown-trigger
                                      className="p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                                      onClick={(e) =>
                                        toggleDropdown(
                                          e,
                                          thread.id,
                                          index,
                                          threadsInGroup.length
                                        )
                                      }
                                      aria-label="Thread options"
                                    >
                                      <MoreVertical size={14} />
                                    </button>

                                    {openDropdownId === (thread.id) && (
                                      <div
                                        role="menu"
                                        className={`absolute ${
                                          dropdownPosition === "top"
                                            ? "bottom-full mb-1"
                                            : "top-full mt-1"
                                        } right-0 bg-gray-50 dark:bg-neutral-800 shadow-lg rounded-lg py-1 min-w-36 z-10 border border-neutral-200 dark:border-neutral-700`}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          className="w-full text-left px-3 py-1.5 hover:bg-white dark:hover:bg-neutral-700 flex items-center text-xs text-neutral-900 dark:text-white transition-colors"
                                          onClick={(e) =>
                                            handleMenuAction(
                                              e,
                                              "star",
                                              thread.id
                                            )
                                          }
                                        >
                                          <Star 
                                            size={14} 
                                            className={`mr-2 flex-shrink-0 ${starredThreads.includes(thread.id) ? 'text-yellow-400 fill-yellow-400' : ''}`} 
                                          /> 
                                          <span>{starredThreads.includes(thread.id) ? 'Unstar thread' : 'Star thread'}</span>
                                        </button>
                                        <button
                                          className="w-full text-left px-3 py-1.5 hover:bg-white dark:hover:bg-neutral-700 flex items-center text-xs text-neutral-900 dark:text-white transition-colors"
                                          onClick={(e) =>
                                            handleMenuAction(
                                              e,
                                              "rename",
                                              thread.id
                                            )
                                          }
                                        >
                                          <Edit size={14} className="mr-2 flex-shrink-0" /> 
                                          <span>Rename thread</span>
                                        </button>
                                        <button
                                          className={`w-full text-left px-3 py-1.5 hover:bg-white dark:hover:bg-neutral-700 flex items-center text-xs text-red-500 transition-colors ${ (threads?.length || 0) <= 1 ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                                          disabled={(threads?.length || 0) <= 1}
                                          onClick={(e) =>
                                            handleMenuAction(
                                              e,
                                              "delete",
                                              thread.id
                                            )
                                          }
                                        >
                                          <Trash size={14} className="mr-2 flex-shrink-0" /> 
                                          <span>Delete thread</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Create thread button - expanded view */}
          <div className="px-3 pb-4 pt-2">
            <button
              className={`rounded-xl px-4 py-2 flex items-center text-sm font-medium transition-all duration-200 shadow-sm w-full justify-center ${
                isReadOnly 
                  ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed' 
                  : 'bg-brand-action hover:bg-brand-action-hover text-brand-action-text'
              }`}
              style={{ marginBottom: '8px' }}
              onClick={handleNewThreadClick}
              disabled={isReadOnly || loading}
              title={isReadOnly ? 'Upgrade to create new threads' : loading ? 'Processing...' : 'Create a new thread'}
              data-tour="new-thread-button"
            >
              <Plus size={16} className="mr-1.5" />
              New Thread
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Collapsed view */}
          <div className="p-2 flex justify-center">
            <button
              className="p-1.5 rounded-lg transition-colors hover:bg-slate-200 dark:hover:bg-neutral-800 cursor-pointer"
              onClick={() => setSidebarOpen(true)}
              title="Expand panel"
            >
              <ChevronRight
                size={16}
                className="text-neutral-600 dark:text-neutral-400"
              />
            </button>
          </div>

          {/* Container with fixed height and absolutely positioned elements */}
          <div className="flex-1 h-full relative">

            {/* Thread icons in collapsed mode - now scrollable with hidden scrollbar - fixed top position and height */}
            <div className="absolute top-1 left-0 right-0 bottom-16 flex flex-col space-y-2 items-center px-1.5 overflow-y-scroll hide-scrollbar">
              {fetchingThreads ? (
                <div className="text-neutral-500 text-xs">
                  ...
                </div>
              ) : error ? (
                <div className="text-red-500 text-xs">!</div>
              ) : threads && threads.length > 0 ? (
                // Group threads by date as we do in expanded view
                Object.entries(groupThreadsByDate(threads)).map(([timeframe, threadsInGroup]) => {
                  if (threadsInGroup.length === 0) return null;
                  
                  // If section is collapsed, show a single icon with count for any group
                  if (!expandedSections[timeframe]) {
                    const getGroupIcon = (timeframe) => {
                      switch(timeframe) {
                        case 'starred':
                          return <Star size={16} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.starred : uiColors.icon} ${timeframe === 'starred' ? 'fill-current' : ''}`} />;
                        case 'today':
                          return <Sun size={16} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.today : uiColors.icon}`} />;
                        case 'yesterday':
                          return <History size={16} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.yesterday : uiColors.icon}`} />;
                        case 'older':
                          return <Archive size={16} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.older : uiColors.icon}`} />;
                        default:
                          return null;
                      }
                    };

                    return (
                      <div
                        key={`group-${timeframe}`}
                        onClick={(e) => {
                          // Only toggle the section without expanding sidebar
                          e.stopPropagation();
                          toggleSection(timeframe);
                        }}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition-all text-xs relative ${'bg-neutral-300 dark:bg-neutral-800/40 hover:bg-neutral-400 dark:hover:bg-neutral-700/50 text-neutral-700 dark:text-neutral-300'} mb-1.5 ring-2 ring-transparent hover:ring-white/20`}
                        title={`${threadsInGroup.length} ${timeframe === 'starred' ? 'starred' : timeframe === 'today' ? "today's" : timeframe === 'yesterday' ? "yesterday's" : 'archived'} thread${threadsInGroup.length !== 1 ? 's' : ''}`}
                      >
                        <div className="flex flex-col items-center justify-center">
                          {getGroupIcon(timeframe)}
                          <span className={`text-[10px] font-semibold mt-1 ${uiColors.text}`}>{threadsInGroup.length}</span>
                        </div>
                        {/* Indicator if any thread in the group has running jobs */}
                        {threadsInGroup.some(thread => thread.running_jobs && thread.running_jobs.length > 0) && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-600 rounded-full animate-pulse shadow-lg shadow-green-500/50 border-2 border-white dark:border-neutral-800"></div>
                        )}
                      </div>
                    );
                  }
                  
                  // If section is expanded, show individual threads
                  return (
                    <div key={`expanded-${timeframe}`} className="relative w-full mb-1.5">
                      {/* Group header badge for expanded sections */}
                       <div className="flex justify-center mb-1.5 mt-0">
                        <div 
                          onClick={(e) => {
                            // Allow collapsing the section when clicked
                            e.stopPropagation();
                            toggleSection(timeframe);
                          }}
                          className={`flex items-center justify-center w-10 h-10 rounded-lg ${'bg-neutral-300 dark:bg-neutral-800/40 hover:bg-neutral-400 dark:hover:bg-neutral-700/50 text-neutral-700 dark:text-neutral-300'} cursor-pointer transition-opacity`}>
                          <div className="flex flex-col items-center justify-center">
                            {(() => {
                              const iconSize = 16;
                              switch(timeframe) {
                                case 'starred':
                                  return <Star size={iconSize} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.starred : uiColors.icon} ${timeframe === 'starred' ? 'fill-current' : ''}`} />;
                                case 'today':
                                  return <Sun size={iconSize} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.today : uiColors.icon}`} />;
                                case 'yesterday':
                                  return <History size={iconSize} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.yesterday : uiColors.icon}`} />;
                                case 'older':
                                  return <Archive size={iconSize} className={`${timeframe === getActiveThreadGroup() ? groupIconColors.older : uiColors.icon}`} />;
                                default:
                                  return null;
                              }
                            })()}
                            <span className={`text-[10px] font-semibold mt-1 ${uiColors.text}`}>{threadsInGroup.length}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Subtle background wrapper for the group */}
                      <div className={`relative rounded-lg mx-0.5 px-1 py-1.5 bg-neutral-100/30 dark:bg-neutral-800/20`}>
                        {/* Removed vertical line indicator */}
                        
                        <div className="space-y-1.5 overflow-visible">
                          {threadsInGroup.map((thread) => (
                          <Tooltip title={`${thread.title} [${thread.created_at ? new Date(thread.created_at).toLocaleString() : 'No date'}]`} enterDelay={800} placement="right" key={thread.id}>
                            <div
                              key={thread.id}
                              onClick={() => {
                                navigate(`/project/${workspaceId}/thread/${thread.id}`);
                              }}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all text-xs relative ${
                                thread.id === threadId
                                  ? "bg-slate-300 dark:bg-neutral-700 text-neutral-900 dark:text-white ring-2 ring-lime-500"
                                  : `${'bg-neutral-300 dark:bg-neutral-800/40 hover:bg-neutral-400 dark:hover:bg-neutral-700/50 text-neutral-700 dark:text-neutral-300'} hover:scale-110`
                              }`}
                              title={thread.title || `Thread ${thread.id}`}
                            >
                              <div className="flex items-center justify-center w-full h-full">
                                {thread.agent_type === 'coding_agent' ? (
                                  <Code size={14} className="text-accent-purple" />
                                ) : (
                                  <MessageSquare size={14} />
                                )}
                              </div>
                              {/* Running job indicator for collapsed view */}
                              {thread.running_jobs && thread.running_jobs.length > 0 ? (
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-600 rounded-full animate-pulse shadow-lg shadow-green-500/50 border-2 border-white dark:border-neutral-800"></div>
                              ) : (
                                starredThreads.includes(thread.id) && (
                                  <div className="absolute -top-1 -right-1">
                                    <Star size={8} className="text-yellow-400 fill-yellow-400" />
                                  </div>
                                )
                              )}
                            </div>
                          </Tooltip>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-neutral-500 text-xs">
                  No threads
                </div>
              )}
            </div>
            
            {/* New thread button - collapsed view, exactly aligned with expanded view */}
            <div className="absolute bottom-0 left-0 right-0 bg-slate-100 dark:bg-neutral-900 z-10 px-3 pb-4 pt-2">

              <button
                onClick={handleNewThreadClick}
                style={{ marginBottom: '8px' }} 
                className={`w-full h-8 flex items-center justify-center rounded-full shadow-sm transition-all ${
                  isReadOnly || loading
                    ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed' 
                    : 'bg-brand-action hover:bg-brand-action-hover text-brand-action-text'
                }`}
                disabled={isReadOnly || loading}
                title={isReadOnly ? 'Upgrade to create new threads' : loading ? 'Processing...' : 'New thread'}
                data-tour="new-thread-button"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
        </>
      )}
      <DeleteConfirmDialog
        open={deleteConfirmDialog.open}
        title={deleteConfirmDialog.title}
        message={deleteConfirmDialog.message}
        onConfirm={() => {
          deleteConfirmDialog.onConfirm();
          setDeleteConfirmDialog((prev) => ({ ...prev, open: false }));
        }}
        onClose={() =>
          setDeleteConfirmDialog((prev) => ({ ...prev, open: false }))
        }
      />

      <RenameDialog
        open={renameDialog.open}
        title={renameDialog.title}
        initialValue={renameDialog.initialValue}
        onConfirm={handleRenameThread}
        onClose={() => setRenameDialog(prev => ({ ...prev, open: false }))}
      />

    </aside>
  );
};

export default ThreadsPanel;
